/**
 * CEO Command Center v11 - Sync Status Component
 *
 * Displays real-time P2P sync status in the header.
 * Shows connection status, peer count, sync activity, and peer connection UI.
 *
 * @module components/common/SyncStatus
 * @version 11.0.0
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSyncStore } from '@/stores/syncStore';
import { syncService, transportService } from '@/services/sync';
import { cn } from '@/utils/format';
import type { SyncStatus as SyncStatusType } from '@/types/sync';

// ============================================
// SYNC STATUS INDICATOR
// ============================================

interface SyncStatusIndicatorProps {
  status: SyncStatusType;
  peerCount: number;
  className?: string;
}

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
  status,
  peerCount,
  className,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          color: 'bg-green-500',
          textColor: 'text-green-600',
          label: peerCount > 0 ? `${peerCount} peer${peerCount !== 1 ? 's' : ''}` : 'Online',
          icon: '●',
        };
      case 'syncing':
        return {
          color: 'bg-blue-500 animate-pulse',
          textColor: 'text-blue-600',
          label: 'Syncing...',
          icon: '◐',
        };
      case 'connecting':
        return {
          color: 'bg-yellow-500 animate-pulse',
          textColor: 'text-yellow-600',
          label: 'Connecting...',
          icon: '○',
        };
      case 'error':
        return {
          color: 'bg-red-500',
          textColor: 'text-red-600',
          label: 'Error',
          icon: '✕',
        };
      default:
        return {
          color: 'bg-gray-400',
          textColor: 'text-gray-500',
          label: 'Offline',
          icon: '○',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <span className={cn('w-2 h-2 rounded-full transition-colors', config.color)} />
      <span className={cn('text-xs font-medium transition-colors', config.textColor)}>
        {config.label}
      </span>
    </div>
  );
};

// ============================================
// PEER CONNECTION DIALOG
// ============================================

interface PeerConnectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  myPeerId: string | null;
  onConnect: (peerId: string) => Promise<void>;
}

const PeerConnectionDialog: React.FC<PeerConnectionDialogProps> = ({
  isOpen,
  onClose,
  myPeerId,
  onConnect,
}) => {
  const [remotePeerId, setRemotePeerId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleConnect = async () => {
    if (!remotePeerId.trim()) return;

    setConnecting(true);
    setError(null);

    try {
      await onConnect(remotePeerId.trim());
      setRemotePeerId('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  const handleCopyPeerId = async () => {
    if (myPeerId) {
      await navigator.clipboard.writeText(myPeerId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Connect to Peer</h2>

        {/* My Peer ID */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Your Peer ID</label>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={myPeerId || 'Not connected'}
              className="flex-1 px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600 font-mono"
            />
            <button
              onClick={handleCopyPeerId}
              disabled={!myPeerId}
              className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Share this ID with others to let them connect to you</p>
        </div>

        {/* Remote Peer ID */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Remote Peer ID</label>
          <input
            type="text"
            value={remotePeerId}
            onChange={(e) => setRemotePeerId(e.target.value)}
            placeholder="Enter peer ID to connect..."
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={!remotePeerId.trim() || connecting}
            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SYNC STATUS DROPDOWN
// ============================================

interface SyncStatusDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  status: SyncStatusType;
  peerCount: number;
  connectedPeers: string[];
  lastSyncAt: number | null;
  pendingChanges: number;
  myPeerId: string | null;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onOpenPeerDialog?: () => void;
  onOpenSettings?: () => void;
  onDisconnectPeer?: (peerId: string) => void;
}

const SyncStatusDropdown: React.FC<SyncStatusDropdownProps> = ({
  isOpen,
  onClose,
  status,
  peerCount,
  connectedPeers,
  lastSyncAt,
  pendingChanges,
  myPeerId,
  onConnect,
  onDisconnect,
  onOpenPeerDialog,
  onOpenSettings,
  onDisconnectPeer,
}) => {
  if (!isOpen) return null;

  const formatLastSync = () => {
    if (!lastSyncAt) return 'Never';
    const diff = Date.now() - lastSyncAt;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return `${Math.floor(diff / 3600000)}h ago`;
  };

  const truncatePeerId = (id: string) => {
    if (id.length <= 20) return id;
    return `${id.slice(0, 10)}...${id.slice(-6)}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Dropdown */}
      <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">P2P Sync</h3>
            {onOpenSettings && (
              <button
                onClick={() => {
                  onOpenSettings();
                  onClose();
                }}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Settings
              </button>
            )}
          </div>

          <div className="space-y-3">
            {/* Connection status */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Status</span>
              <span
                className={cn(
                  'font-medium',
                  status === 'connected'
                    ? 'text-green-600'
                    : status === 'syncing'
                      ? 'text-blue-600'
                      : status === 'error'
                        ? 'text-red-600'
                        : 'text-gray-500'
                )}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>

            {/* My Peer ID */}
            {myPeerId && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">My ID</span>
                <span className="font-mono text-xs text-gray-600" title={myPeerId}>
                  {truncatePeerId(myPeerId)}
                </span>
              </div>
            )}

            {/* Last sync */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Last sync</span>
              <span className="font-medium text-gray-900">{formatLastSync()}</span>
            </div>

            {/* Pending changes */}
            {pendingChanges > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Pending changes</span>
                <span className="font-medium text-amber-600">{pendingChanges}</span>
              </div>
            )}
          </div>

          {/* Connected Peers */}
          {connectedPeers.length > 0 && (
            <div className="mt-4 pt-3 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Connected Peers ({peerCount})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {connectedPeers.map((peerId) => (
                  <div
                    key={peerId}
                    className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs"
                  >
                    <span className="font-mono text-gray-600" title={peerId}>
                      {truncatePeerId(peerId)}
                    </span>
                    {onDisconnectPeer && (
                      <button
                        onClick={() => onDisconnectPeer(peerId)}
                        className="text-red-500 hover:text-red-700"
                        title="Disconnect"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 pt-3 border-t space-y-2">
            {status === 'disconnected' ? (
              <button
                onClick={onConnect}
                className="w-full px-3 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
              >
                Start Sync
              </button>
            ) : (
              <>
                <button
                  onClick={onOpenPeerDialog}
                  className="w-full px-3 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Connect to Peer
                </button>
                <button
                  onClick={onDisconnect}
                  className="w-full px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// MAIN SYNC STATUS COMPONENT
// ============================================

interface SyncStatusProps {
  className?: string;
  compact?: boolean;
  onOpenSettings?: () => void;
}

export const SyncStatus: React.FC<SyncStatusProps> = ({
  className,
  compact = false,
  onOpenSettings,
}) => {
  const { identity, activeSpace, status, pendingChanges, lastSyncAt, startSync, stopSync } =
    useSyncStore();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPeerDialogOpen, setIsPeerDialogOpen] = useState(false);
  const [peerCount, setPeerCount] = useState(0);
  const [connectedPeers, setConnectedPeers] = useState<string[]>([]);
  const [myPeerId, setMyPeerId] = useState<string | null>(null);

  // Initialize sync service when identity and space are available
  useEffect(() => {
    if (identity && activeSpace && status === 'disconnected') {
      const initSync = async () => {
        try {
          await syncService.init(identity, [activeSpace]);
          await startSync();
        } catch (err) {
          console.error('[SyncStatus] Failed to initialize sync:', err);
        }
      };
      initSync();
    }
  }, [identity, activeSpace, status, startSync]);

  // Update peer info from services
  useEffect(() => {
    const updatePeerInfo = () => {
      setPeerCount(syncService.getPeerCount());
      setConnectedPeers(transportService.getConnectedPeers());
      setMyPeerId(transportService.getPeerId());
    };

    updatePeerInfo();
    const interval = setInterval(updatePeerInfo, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleConnect = useCallback(async () => {
    if (identity && activeSpace) {
      try {
        await syncService.init(identity, [activeSpace]);
        await startSync();
      } catch (err) {
        console.error('[SyncStatus] Failed to connect:', err);
      }
    }
    setIsDropdownOpen(false);
  }, [identity, activeSpace, startSync]);

  const handleDisconnect = useCallback(() => {
    syncService.shutdown();
    stopSync();
    setIsDropdownOpen(false);
  }, [stopSync]);

  const handleConnectToPeer = useCallback(async (peerId: string) => {
    await syncService.connectToPeer(peerId);
  }, []);

  const handleDisconnectPeer = useCallback((peerId: string) => {
    transportService.disconnect(peerId);
  }, []);

  // Show setup prompt if no identity
  if (!identity) {
    return (
      <div className={cn('relative', className)}>
        <button
          onClick={() => onOpenSettings?.()}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-amber-600 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
          title="Set up P2P sync"
        >
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span>Setup P2P</span>
        </button>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={cn('relative', className)}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
        >
          <SyncStatusIndicator status={status} peerCount={peerCount} />
        </button>
        <SyncStatusDropdown
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          status={status}
          peerCount={peerCount}
          connectedPeers={connectedPeers}
          lastSyncAt={lastSyncAt}
          pendingChanges={pendingChanges}
          myPeerId={myPeerId}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onOpenPeerDialog={() => {
            setIsDropdownOpen(false);
            setIsPeerDialogOpen(true);
          }}
          onOpenSettings={onOpenSettings}
          onDisconnectPeer={handleDisconnectPeer}
        />
        <PeerConnectionDialog
          isOpen={isPeerDialogOpen}
          onClose={() => setIsPeerDialogOpen(false)}
          myPeerId={myPeerId}
          onConnect={handleConnectToPeer}
        />
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors',
          isDropdownOpen && 'bg-gray-100'
        )}
      >
        <SyncStatusIndicator status={status} peerCount={peerCount} />
      </button>
      <SyncStatusDropdown
        isOpen={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        status={status}
        peerCount={peerCount}
        connectedPeers={connectedPeers}
        lastSyncAt={lastSyncAt}
        pendingChanges={pendingChanges}
        myPeerId={myPeerId}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        onOpenPeerDialog={() => {
          setIsDropdownOpen(false);
          setIsPeerDialogOpen(true);
        }}
        onOpenSettings={onOpenSettings}
        onDisconnectPeer={handleDisconnectPeer}
      />
      <PeerConnectionDialog
        isOpen={isPeerDialogOpen}
        onClose={() => setIsPeerDialogOpen(false)}
        myPeerId={myPeerId}
        onConnect={handleConnectToPeer}
      />
    </div>
  );
};

export default SyncStatus;
